/**
 * D454: the two trees as plain data for the screens. The Almanac's four branches (production,
 * tapping, hands, festival) and the permanent tree's four (start, taps, growth, land) are each a
 * stack of tiers of steps. The sim says what state every step is in (`idleAlmanacTree`,
 * `idlePerkTree`); this file puts words on it: a tile's foot line, a tier's status line, and the
 * ruled lines of the sheet a tap opens. No state of its own, so the unit tests can read it.
 */
import {
  idleAlmanacTree,
  idleEffects,
  idleFestivalsThisGeneration,
  idleMasteryCost,
  idlePerkCost,
  idlePerkTotal,
  idlePerkTree,
  idleUpgradeCost,
  idleUpgradeTotal,
  type IdleAlmanacBranchView,
  type IdleStepNeeds,
  type IdleWorldState,
} from '@hedgerow/sim';
import type { IdleContent } from '@hedgerow/data';
import { heritageMarks } from './badges.ts';
import { coins, num } from './notation.ts';
import { cap, lower, t as term } from './terms.ts';
import { effectText, iconFor, opensAfter, perkText, totalText } from './view.ts';

/** How a tile is drawn. `short` is open but not affordable. */
export type TileState = 'bought' | 'open' | 'short' | 'locked' | 'closed';

export interface TreeTile {
  id: string;
  /** The effect in plain words, the first sentence only: the sheet has all of it. */
  text: string;
  icon: string;
  state: TileState;
  /** The foot line: a price, `Bought`, `Needs 640Kc`, or what opens it. */
  foot: string;
  /** The foot's colour role. */
  tone: 'accent' | 'green' | 'ink' | 'muted';
  /** A permanent step's level, under its name (`level 3`). */
  level?: string;
  fork?: string;
  /** D472: a perk at its top level; nothing more to do with it. */
  top?: boolean;
}

export interface TreeTier {
  tier: number;
  head: string;
  /** `2 of 6 bought`, or what a shut tier waits for. */
  status: string;
  /** A shut tier's progress towards opening, 0 to 1; absent for an open one. */
  progress?: number;
  shut: boolean;
  tiles: TreeTile[];
  /** A permanent tier holding a fork says how it closes. */
  forkNote?: string;
}

/** D457: an Almanac branch's mastery step, a full-width tile at the branch's foot. */
export interface TreeMastery {
  branch: string;
  /** `Production · mastery`. */
  kicker: string;
  /** `Production mastery`. */
  title: string;
  /** `level 4 · everything pays 1.05× a level`. */
  line: string;
  /** `Level 5`. */
  action: string;
  /** The next level's price; null while it is shut. */
  price: string | null;
  /** Why the button is off: `Needs 640Kc`, or what opens it (`After tier 6: 4 of 6`). */
  off?: string;
}

/** D457: the tier the next generation opens, as a dashed card under the mastery step. */
export interface TreeNext {
  title: string;
  text: string;
}

export interface TreeBranch {
  id: string;
  name: string;
  /** `4 of 21`. */
  count: string;
  bought: number;
  total: number;
  progress: number;
  tiers: TreeTier[];
  mastery?: TreeMastery;
  next?: TreeNext;
}

/** A sheet's total as ruled facts: `Now 1.5×, next 2×` becomes `Now: 1.5×` and `Next: 2×`. */
function pushTotal(lines: string[], total: string | null): void {
  if (!total) return;
  const m = /^Now (.*?)(?:, next (.*))?$/.exec(total);
  if (!m) {
    lines.push(total);
    return;
  }
  lines.push(`Now: ${m[1]!}`);
  if (m[2] !== undefined) lines.push(`Next: ${m[2]}`);
}

/** D459: a sheet line as a label and a value; a line with no label is a note across both columns. */
export function splitFact(line: string): { label: string; value: string } | { note: string } {
  const m = /^([^.:]+): (.+)$/.exec(line);
  return m ? { label: m[1]!, value: m[2]! } : { note: line };
}

/** What a step's sheet shows. */
export interface TreeSheet {
  id: string;
  /** `Production · tier 2`. */
  kicker: string;
  /** The whole effect in words. */
  title: string;
  icon: string;
  /** The ruled lines of what it changes. */
  lines: string[];
  state: TileState;
  /** The price of the next purchase; null when there is none to make. */
  price: string | null;
  /** Why Buy is off: `Needs 640Kc`, or what opens it. Absent when Buy works. */
  off?: string;
  /** Set when bought outright and nothing more can be bought. */
  done?: string;
}

export interface TreeModel {
  branches: TreeBranch[];
  sheets: Record<string, TreeSheet>;
}

const first = (text: string): string => text.split('. ')[0]!.replace(/\.$/, '');

const branchName = (content: IdleContent, id: string): string => content.branches[id] ?? cap(id);

const closedWords = (): string => `Closed this ${term('generation')}`;

/** `2h 10m`, `45m`: a wait in whole minutes. */
function waitWords(ms: number): string {
  const m = Math.max(1, Math.ceil(ms / 60_000));
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
}

const producerName = (content: IdleContent, id: string): string =>
  (content.producers.find((p) => p.id === id)?.name ?? id).toLowerCase();

const perkName = (content: IdleContent, id: string): string =>
  (content.perks.find((p) => p.id === id)?.name ?? id).toLowerCase();

const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth'];

/** D457: `third`, `ninth`, `12th`. */
export function ordinal(n: number): string {
  if (n <= ORDINALS.length) return ORDINALS[n - 1]!;
  if (n === 9) return 'ninth';
  if (n === 10) return 'tenth';
  return `${n}th`;
}

/** D457, D463: `Opens in generation 3`. */
export function generationWords(generation: number): string {
  return `Opens in ${term('generation')} ${generation}`;
}

/**
 * D457: a step's short form for its tile and the goal strip ("Wheat pays 1.5×"), from the
 * theme's data. The step sheet keeps the full sentence.
 */
export function stepShort(content: IdleContent, id: string): string {
  const u = content.upgrades.find((x) => x.id === id);
  return u ? u.short : id;
}

/**
 * What holds an Almanac step back, in words: the condition met last (D457). A later generation
 * first, then the tier below with its progress ("After tier 5: 2 of 3"), then the step's own.
 * `below` is the tier just under the step: what it has bought and needs.
 */
export function stepLockWords(
  state: IdleWorldState,
  content: IdleContent,
  needs: IdleStepNeeds,
  stepTier?: number,
  below?: { have: number; need: number },
): string {
  if (needs.generation) return generationWords(needs.generation);
  // `needs.tier` names the first shut tier; a step deeper down waits on the tier just below it.
  if (needs.tier) {
    const tier = stepTier ? stepTier - 1 : needs.tier.tier;
    const b = below ?? needs.tier;
    return `${Math.min(b.have, b.need)} of ${b.need} in tier ${tier} bought`;
  }
  if (needs.producer) return `Opens when you own ${producerName(content, needs.producer)}`;
  if (needs.owned)
    return `Opens at ${needs.owned.count} ${producerName(content, needs.owned.producer)}`;
  if (needs.festivals) return opensAfter(needs.festivals, idleFestivalsThisGeneration(state));
  if (needs.perk) return `Needs ${perkName(content, needs.perk)} first`;
  if (needs.yearMs) return `Opens in ${waitWords(needs.yearMs)}`;
  return 'Locked';
}

const iconOfPerk = (content: IdleContent, id: string): string => {
  const perk = content.perks.find((p) => p.id === id);
  const producer = perk?.effect?.producer ?? perk?.startProducers?.[0] ?? perk?.startHands?.[0];
  return producer ? iconFor(producer) : 'sparkles';
};

/** The Almanac's tree for this year, every step with its tile and its sheet. */
export function almanacModel(state: IdleWorldState, content: IdleContent): TreeModel {
  const effects = idleEffects(state, content, state.advancedTo);
  const have = state.year.coins;
  const sheets: Record<string, TreeSheet> = {};
  const branches = idleAlmanacTree(state, content).map((b): TreeBranch => {
    const name = branchName(content, b.branch);
    const last = b.tiers.length;
    const tiers = b.tiers.map((t): TreeTier => {
      const tiles = t.steps.map((s): TreeTile => {
        const u = content.upgrades.find((x) => x.id === s.id)!;
        const full = effectText(content, u.effect);
        const cost = idleUpgradeCost(state, content, s.id, effects);
        const tile: TreeTile = {
          id: s.id,
          text: u.short,
          icon: u.effect.producer ? iconFor(u.effect.producer) : 'sparkles',
          state: 'open',
          foot: coins(cost),
          tone: 'accent',
        };
        if (s.fork !== undefined) tile.fork = s.fork;
        const sheet: TreeSheet = {
          id: s.id,
          kicker: `${name} · tier ${t.tier}`,
          title: full,
          icon: tile.icon,
          lines: [],
          state: 'open',
          price: coins(cost),
        };
        const total = totalText(idleUpgradeTotal(state, content, s.id));
        pushTotal(sheet.lines, total);
        if (t.tier < last) {
          const next = b.tiers[t.tier]!;
          const had = Math.min(t.bought + (s.state === 'bought' ? 0 : 1), t.need);
          sheet.lines.push(`Tier ${next.tier}: ${had} of ${t.need} bought`);
        }
        if (s.fork !== undefined && s.state !== 'bought')
          sheet.lines.push(`Choose one. The other stays closed this ${term('generation')}.`);
        if (s.state === 'bought') {
          tile.state = sheet.state = 'bought';
          tile.foot = 'Bought';
          tile.tone = 'green';
          sheet.price = null;
          sheet.done = 'Bought';
        } else if (s.state === 'closed') {
          tile.state = sheet.state = 'closed';
          tile.foot = closedWords();
          tile.tone = 'muted';
          sheet.off = closedWords();
        } else if (s.state === 'locked') {
          const under = b.tiers[t.tier - 2];
          const below = under ? { have: under.bought, need: under.need } : undefined;
          const words = s.needs ? stepLockWords(state, content, s.needs, t.tier, below) : 'Locked';
          tile.state = sheet.state = 'locked';
          tile.foot = words;
          tile.tone = 'muted';
          sheet.off = words;
        } else if (cost > have) {
          tile.state = sheet.state = 'short';
          tile.foot = `Needs ${coins(cost - have)}`;
          tile.tone = 'muted';
          sheet.off = tile.foot;
        }
        sheets[s.id] = sheet;
        return tile;
      });
      const out: TreeTier = {
        tier: t.tier,
        head: `Tier ${t.tier}`,
        status: `${t.bought} of ${t.size} bought`,
        shut: !t.open,
        tiles,
      };
      if (!t.open) {
        const o = t.opensAfter;
        out.status = o
          ? `Opens after ${o.need} in tier ${o.tier} · ${o.have} of ${o.need}`
          : `Opens after tier ${t.tier - 1}`;
        out.progress = o ? Math.min(1, o.have / o.need) : 0;
      }
      return out;
    });
    const branch: TreeBranch = {
      id: b.branch,
      name,
      count: `${b.bought} of ${b.total}`,
      bought: b.bought,
      total: b.total,
      progress: b.total > 0 ? b.bought / b.total : 0,
      tiers,
      mastery: masteryTile(state, content, b, name, effects),
    };
    if (b.next)
      branch.next = {
        title: `Tier ${b.next.tier} ${lower(generationWords(b.next.generation))}`,
        text: `Each ${term('generation')} adds a tier to every branch, and new land.`,
      };
    return branch;
  });
  return { branches, sheets };
}

/** D457: what one mastery level lifts, in words. */
function masteryLift(stat: string, factor: number): string {
  const f = `${Number(factor.toFixed(2))}×`;
  if (stat === 'tap') return `taps pay ${f} a level`;
  if (stat === 'speed') return `everything works ${f} faster a level`;
  if (stat === 'hand')
    return `${term('hands')} cost ${Math.round((1 - factor) * 100)}% less a level`;
  return `everything pays ${f} a level`;
}

/** D457: a branch's mastery step: its level, what each adds, and the next level's button. */
function masteryTile(
  state: IdleWorldState,
  content: IdleContent,
  b: IdleAlmanacBranchView,
  name: string,
  effects: ReturnType<typeof idleEffects>,
): TreeMastery {
  const m = b.mastery;
  const out: TreeMastery = {
    branch: b.branch,
    kicker: `${name} · mastery`,
    title: `${name} mastery`,
    line: `level ${m.level} · ${masteryLift(m.stat, m.factor)}`,
    action: `Level ${m.level + 1}`,
    price: null,
  };
  if (!m.open) {
    out.off = `${m.done} of ${m.size} in tier ${m.tier} bought`;
    return out;
  }
  const cost = idleMasteryCost(state, content, b.branch, effects);
  out.price = coins(cost);
  if (cost > state.year.coins) out.off = `Needs ${coins(cost - state.year.coins)}`;
  return out;
}

/** The permanent tree, every perk with its tile and its sheet. Prices are Heritage, the amount alone. */
export function perkModel(state: IdleWorldState, content: IdleContent): TreeModel {
  const have = state.permanent.heritage;
  const sheets: Record<string, TreeSheet> = {};
  const branches = idlePerkTree(state, content).map((b): TreeBranch => {
    const name = branchName(content, b.branch);
    const tiers = b.tiers.map((t): TreeTier => {
      const tiles = t.steps.map((s): TreeTile => {
        const perk = content.perks.find((x) => x.id === s.id)!;
        const full = perkText(content, perk);
        const oneOff = perk.maxLevel === 1;
        const cost = s.atTop ? null : idlePerkCost(perk, s.level);
        const tile: TreeTile = {
          id: s.id,
          text: first(full),
          icon: iconOfPerk(content, s.id),
          state: 'open',
          foot: cost === null ? '' : num(cost),
          tone: 'accent',
        };
        if (s.fork !== undefined) tile.fork = s.fork;
        if (s.level > 0 && !oneOff) tile.level = `level ${s.level}`;
        const sheet: TreeSheet = {
          id: s.id,
          kicker: `${name} · tier ${t.tier}`,
          title: full,
          icon: tile.icon,
          lines: [],
          state: 'open',
          price: cost === null ? null : num(cost),
        };
        const total = totalText(idlePerkTotal(state, content, s.id));
        pushTotal(sheet.lines, total);
        if (s.level > 0 && !oneOff) sheet.lines.push(`Level: ${s.level}`);
        if (s.fork !== undefined && s.state !== 'bought' && s.state !== 'closed')
          sheet.lines.push(`Choose one. The other stays closed this ${term('generation')}.`);
        if (s.state === 'closed') {
          tile.state = sheet.state = 'closed';
          tile.foot = closedWords();
          tile.tone = 'muted';
          sheet.off = closedWords();
        } else if (s.state === 'locked') {
          const needs = s.needs;
          const words = needs?.tier
            ? `Opens at ${num(needs.tier.at)} spent`
            : needs?.festivals
              ? opensAfter(needs.festivals, idleFestivalsThisGeneration(state))
              : needs?.perk
                ? `Needs ${perkName(content, needs.perk)} first`
                : 'Locked';
          tile.state = sheet.state = 'locked';
          tile.foot = s.fork !== undefined && needs?.tier ? 'Choose one' : words;
          tile.tone = 'muted';
          sheet.off = needs?.tier
            ? `${num(needs.tier.spent)} of ${num(needs.tier.at)} spent`
            : words;
        } else {
          if (s.level > 0) tile.state = sheet.state = 'bought';
          if (cost === null) {
            tile.foot = oneOff ? 'Owned' : 'Top level';
            if (!oneOff) tile.top = true;
            tile.tone = 'green';
            sheet.done = tile.foot;
          } else if (cost > have) {
            tile.foot = `Needs ${num(cost - have)}`;
            tile.tone = 'muted';
            if (s.level === 0) tile.state = sheet.state = 'short';
            sheet.off = tile.foot;
          }
        }
        sheets[s.id] = sheet;
        return tile;
      });
      const owned = t.steps.filter((s) => s.level > 0).length;
      const out: TreeTier = {
        tier: t.tier,
        head: `Tier ${t.tier}`,
        status: `${owned} of ${t.steps.length} bought`,
        shut: !t.open,
        tiles,
      };
      if (!t.open) {
        out.status = `${num(t.spent)} of ${num(t.at)} spent`;
        out.progress = t.at > 0 ? Math.min(1, t.spent / t.at) : 1;
      }
      if (t.steps.some((s) => s.fork !== undefined))
        out.forkNote = `Choose one. The other stays closed this ${term('generation')}.`;
      return out;
    });
    return {
      id: b.branch,
      name,
      count: `${b.bought} of ${b.total}`,
      bought: b.bought,
      total: b.total,
      progress: b.total > 0 ? b.bought / b.total : 0,
      tiers,
    };
  });
  return { branches, sheets };
}

/** D473: the perk tree as Perks and the festival screen both draw it: the model and its marks. */
export function perkTreeProps(world: IdleWorldState, content: IdleContent, marked: boolean) {
  return {
    model: perkModel(world, content),
    marks: marked ? heritageMarks(world, content) : undefined,
  };
}

/** D471: a tier as the Almanac draws it with bought steps hidden. */
export interface TierView {
  tier: TreeTier;
  /** The tiles to draw: the ones not bought. */
  tiles: TreeTile[];
  /** Every step bought: the tier is its header line alone, `all 6 bought`. */
  collapsed: boolean;
  bought: number;
  /** D472: the collapsed header's ending, `all 6 bought` or `all at top level`. */
  label?: string;
}

/**
 * D471: hide a branch's bought steps. A tier with every step bought collapses to its header;
 * a shut tier never does. Mastery is not a tile, so it is never touched. With `reveal` every
 * step shows as before. `bought` is the branch's count, for the "Show bought (n)" row.
 */
export function hideBought(
  tiers: readonly TreeTier[],
  reveal: boolean,
): { tiers: TierView[]; bought: number } {
  let total = 0;
  const out = tiers.map((tier): TierView => {
    const bought = tier.tiles.filter((t) => t.state === 'bought').length;
    total += bought;
    if (reveal) return { tier, tiles: tier.tiles, collapsed: false, bought, label: '' };
    const collapsed = !tier.shut && tier.tiles.length > 0 && bought === tier.tiles.length;
    return {
      tier,
      tiles: collapsed ? [] : tier.tiles.filter((t) => t.state !== 'bought'),
      collapsed,
      bought,
      label: `all ${tier.tiles.length} bought`,
    };
  });
  return { tiers: out, bought: total };
}

/**
 * D472: hide a perk branch's perks at their top level. A tier with every perk at its top
 * level collapses to its header; a shut tier never does. `top` is the branch's count.
 */
export function hideTopLevel(
  tiers: readonly TreeTier[],
  reveal: boolean,
): { tiers: TierView[]; top: number } {
  let total = 0;
  const out = tiers.map((tier): TierView => {
    const top = tier.tiles.filter((t) => t.top).length;
    total += top;
    if (reveal) return { tier, tiles: tier.tiles, collapsed: false, bought: top, label: '' };
    const collapsed = !tier.shut && tier.tiles.length > 0 && top === tier.tiles.length;
    return {
      tier,
      tiles: collapsed ? [] : tier.tiles.filter((t) => !t.top),
      collapsed,
      bought: top,
      label: 'all at top level',
    };
  });
  return { tiers: out, top: total };
}
